import React, { useState, useEffect } from 'react';
import { plantsApi } from '../utils/api';
import { getImageUrl } from '../config/imageConfig';
import CarouselBanner from './common/CarouselBanner';
import './Plants.css';

function Plants() {
  const [featuredPlants, setFeaturedPlants] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // 图片资源 - 使用全局配置处理URL
  const plantsHeroImg = getImageUrl('https://images.unsplash.com/photo-1523348837708-15d4a09cfac2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80');
  const rarePlantImg = getImageUrl('https://images.unsplash.com/photo-1597826368522-9f4cb5a6ba48?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2352&q=80');
  const endemicImg = getImageUrl('https://images.unsplash.com/photo-1518531933037-91b2f5f229cc?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80');
  const conservationImg = getImageUrl('https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2313&q=80');
  const conservationHeroImg = getImageUrl('https://images.unsplash.com/photo-1504567961542-e24d9439a724?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2344&q=80');
  
  // 截断文字的辅助函数
  const truncateText = (text, maxLength = 80) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  }

  // 获取植物数据
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // 获取植物分类
        const categoriesData = await plantsApi.getPlantCategories();
        if (categoriesData && categoriesData.data) {
          setCategories(categoriesData.data);
        }
        
        // 获取特色植物数据，使用与首页相同的处理逻辑
        console.log('开始获取特色植物数据...');
        const plantsResponse = await plantsApi.getFeaturedPlants();
        console.log('特色植物API响应:', plantsResponse);
        
        if (plantsResponse && plantsResponse.code === 200 && plantsResponse.data) {
          let plantsArray = [];
          
          // 处理不同的数据结构
          if (Array.isArray(plantsResponse.data)) {
            plantsArray = plantsResponse.data;
          } else if (plantsResponse.data.records && Array.isArray(plantsResponse.data.records)) {
            plantsArray = plantsResponse.data.records;
          } else if (plantsResponse.data.list && Array.isArray(plantsResponse.data.list)) {
            plantsArray = plantsResponse.data.list;
          }
          
          console.log(`找到 ${plantsArray.length} 个特色植物`);
          
          // 处理植物数据，与首页保持一致
          const processedPlants = plantsArray.map((plant, index) => {
            // 处理图片URL
            let imageUrl = null;
            if (plant.imageUrl) {
              imageUrl = getImageUrl(plant.imageUrl);
            } else {
              const imageFields = [
                plant.mainImageUrl,
                plant.main_image_url,
                plant.mainImage,
                plant.main_image,
                plant.image_url,
                plant.image,
                plant.coverImage,
                plant.cover_image,
                plant.featuredImage,
                plant.featured_image,
                plant.thumbnail,
                plant.thumb
              ];
              
              const rawImageUrl = imageFields.find(field => field && field.length > 0);
              if (rawImageUrl) {
                imageUrl = getImageUrl(rawImageUrl);
              }
            }
            
            // 保护状态映射
            const conservationStatusMap = {
              'CR': '极危',
              'EN': '濒危', 
              'VU': '易危',
              'NT': '近危',
              'LC': '无危'
            };
            
            return {
              ...plant,
              // 确保必要字段存在
              id: plant.id || index,
              name: plant.name || '未知植物',
              scientificName: plant.scientificName || '',
              description: truncateText(plant.description || '暂无描述', 100),
              imageUrl: imageUrl,
              // 保护状态
              conservationStatus: plant.conservationStatus || '',
              conservationStatusText: conservationStatusMap[plant.conservationStatus] || plant.conservationStatus || '',
              // 植物分类
              category: plant.categoryName || plant.category || '',
              categoryEn: plant.categoryNameEn || '',
              // 特色类型
              featuredType: plant.featuredType || '',
              // 花期
              bloomPeriod: plant.bloomPeriod || '',
              // 分布地区
              location: plant.location || '',
              // 显示顺序
              displayOrder: plant.displayOrder || 0,
              // 是否特色展示
              isFeatured: plant.isFeatured || false
            };
          });
          
          // 按displayOrder排序
          const sortedPlants = processedPlants.sort((a, b) => {
            if (a.displayOrder !== b.displayOrder) {
              return a.displayOrder - b.displayOrder;
            }
            return a.id - b.id;
          });
          
          setFeaturedPlants(sortedPlants);
          console.log('特色植物设置成功，共', sortedPlants.length, '个植物');
        } else {
          console.log('未获取到特色植物数据，将显示默认内容');
          setFeaturedPlants([]);
        }
      } catch (err) {
        setError('获取植物数据失败，请稍后重试');
        console.error('获取植物数据失败:', err);
        setFeaturedPlants([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedCategory]);
  
  // 处理分类变更
  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };
  
  // 默认植物数据（当API数据不可用时）
  const defaultPlants = [
    {
      id: 1,
      name: '庐山杜鹃',
      scientificName: 'Rhododendron lushan',
      description: '庐山杜鹃是庐山特有的杜鹃花品种，花期为4-5月，花色鲜艳，是庐山的标志性植物之一。',
      image: rarePlantImg,
      category: '观赏植物',
      conservationStatus: '地方特有'
    },
    {
      id: 2,
      name: '华东黄杉',
      scientificName: 'Pseudotsuga gaussenii',
      description: '国家二级保护植物，为第三纪冰川遗留下来的珍稀树种，在庐山有少量野生分布。',
      image: endemicImg,
      category: '珍稀植物',
      conservationStatus: '国家二级保护'
    },
    {
      id: 3,
      name: '银杏',
      scientificName: 'Ginkgo biloba',
      description: '被称为"活化石"的古老树种，园内有多株百年以上的古银杏树，秋季金黄的扇形叶片尤为美丽。',
      image: conservationImg,
      category: '古树名木',
      conservationStatus: '国家保护'
    },
    {
      id: 4,
      name: '江南油杉',
      scientificName: 'Keteleeria fortunei',
      description: '国家一级保护植物，为中国特有的珍稀濒危植物，我园开展了系统的保护研究工作。',
      image: rarePlantImg,
      category: '珍稀植物',
      conservationStatus: '国家一级保护'
    },
    {
      id: 5,
      name: '珙桐',
      scientificName: 'Davidia involucrata',
      description: '被誉为"植物活化石"和"中国鸽子树"，是国家一级保护植物，花期时白色苞片如鸽子展翅。',
      image: rarePlantImg,
      category: '珍稀植物',
      conservationStatus: '国家一级保护'
    },
    {
      id: 6,
      name: '连香树',
      scientificName: 'Cercidiphyllum japonicum',
      description: '第三纪孑遗植物，国家二级保护植物，秋季叶片变黄时散发出焦糖般的香味。',
      image: endemicImg,
      category: '珍稀植物',
      conservationStatus: '国家二级保护'
    }
  ];
  
  // 使用API数据或默认数据
  const displayPlants = featuredPlants.length > 0 ? featuredPlants : defaultPlants;
  
  return (
    <div>
      {/* Hero Section - 使用轮播图组件 */}
      <CarouselBanner 
        categoryNameEn="ZWZS"
        defaultImage={plantsHeroImg}
        defaultTitle="植物收藏"
        defaultSubtitle="探索我们丰富的植物多样性，包括珍稀濒危植物和庐山特有的本土植物。"
        className="hero-section"
      />
      
      <div className="container">
        <section className="page-section">
          <h2 className="page-title">特色植物</h2>
          <p className="page-subtitle">
            庐山植物园收集保存了众多珍稀植物，是中国重要的植物多样性保护基地之一。
          </p>
          
          {/* 分类筛选 */}
          {categories.length > 0 && (
            <div className="plant-categories">
              <button 
                className={`category-btn ${selectedCategory === 'all' ? 'active' : ''}`}
                onClick={() => handleCategoryChange('all')}
              >
                全部植物
              </button>
              {categories.map(category => (
                <button 
                  key={category.id}
                  className={`category-btn ${selectedCategory === category.name ? 'active' : ''}`}
                  onClick={() => handleCategoryChange(category.name)}
                >
                  {category.name}
                </button>
              ))}
            </div>
          )}
          
          {/* 加载状态 */}
          {loading && (
            <div className="loading-state">
              <p>正在加载植物数据...</p>
            </div>
          )}
          
          {/* 错误状态 */}
          {error && (
            <div className="error-state">
              <p>{error}</p>
              <button onClick={() => window.location.reload()}>重新加载</button>
            </div>
          )}
          
          {/* 植物展示 - 使用与首页相同的样式 */}
          {!loading && !error && (
            <div className="plants-grid">
              {displayPlants.map((plant, index) => {
                // 处理植物图片
                const plantImage = getImageUrl(plant.imageUrl) || plant.image || rarePlantImg;
                
                
                return (
                  <div key={plant.id} className="plant-card">
                    <div className="plant-image">
                      <img 
                        src={plantImage} 
                        alt={plant.name}
                        onError={(e) => { 
                          console.log(`植物图片加载失败: ${plant.name}`);
                          e.target.src = rarePlantImg; 
                        }}
                      />
                      {/* 添加悬浮遮罩显示学名 */}
                      {plant.scientificName && (
                        <div className="plant-overlay">
                          <span className="scientific-name">{plant.scientificName}</span>
                        </div>
                      )}
                    </div>
                    <div className="plant-content">
                      <h3>{plant.name}</h3>
                      {plant.scientificName && (
                        <p className="plant-scientific-name">{plant.scientificName}</p>
                      )}
                      <p className="plant-description">{plant.description}</p>
                      {/* 显示多个标签 */}
                      {/* <div className="plant-tags">
                        {plantTags.map((tag, tagIndex) => (
                          <span key={tagIndex} className={`plant-tag plant-tag-${tag.type}`}>
                            {tag.text}
                          </span>
                        ))}
                      </div> */}
                      {/* 显示额外信息 */}
                      <div className="plant-info">
                        {plant.bloomPeriod && (
                          <span className="plant-bloom-period">
                            🌸 {plant.bloomPeriod}
                          </span>
                        )}
                        {plant.location && (
                          <span className="plant-location" title={plant.location}>
                            📍 {truncateText(plant.location, 20)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          
          <div className="conservation-banner" style={{ backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.7)), url(${conservationHeroImg})` }}>
            <div className="conservation-content">
              <h2>植物保护计划</h2>
              <p>庐山植物园积极参与国家和国际植物保护计划，建立了种子库和植物繁育基地，开展珍稀濒危植物的迁地保护和回归自然工作。</p>
            </div>
          </div>
          
          <div className="conservation-section">
            <div className="conservation-intro">
              <p>
                我们的植物保护团队长期致力于珍稀濒危植物的研究与保护工作。目前已成功保护了30多种国家级珍稀濒危植物，
                为生物多样性保护作出了重要贡献。我们的保护工作涵盖了物种调查、种质资源收集、迁地保护、生态修复等多个方面。
              </p>
            </div>
            
            <div className="conservation-grid">
              <div className="conservation-card">
                <div className="conservation-icon">🌱</div>
                <h3>濒危植物名录</h3>
                <ul className="conservation-list">
                  <li>庐山杜鹃 (Rhododendron lushan)</li>
                  <li>江南油杉 (Keteleeria fortunei)</li>
                  <li>华东黄杉 (Pseudotsuga gaussenii)</li>
                  <li>南方红豆杉 (Taxus wallichiana var. mairei)</li>
                  <li>水杉 (Metasequoia glyptostroboides)</li>
                  <li>银杏 (Ginkgo biloba)</li>
                  <li>伯乐树 (Bretschneidera sinensis)</li>
                  <li>珙桐 (Davidia involucrata)</li>
                </ul>
              </div>
              
              <div className="conservation-card">
                <div className="conservation-icon">🔬</div>
                <h3>保护策略</h3>
                <ul className="conservation-list">
                  <li>建立种子库，保存植物种子</li>
                  <li>组织科考队伍，探索野生植物资源</li>
                  <li>开展植物繁育研究</li>
                  <li>恢复退化生态系统</li>
                  <li>加强公众教育，提高保护意识</li>
                  <li>国际合作交流，共享保护经验</li>
                </ul>
              </div>
              
              <div className="conservation-card">
                <div className="conservation-icon">🌍</div>
                <h3>保护成果</h3>
                <ul className="conservation-list">
                  <li>建立国家级种质资源库</li>
                  <li>成功繁育15种极度濒危植物</li>
                  <li>完成3个自然保护区植被调查</li>
                  <li>发表科研论文48篇</li>
                  <li>举办保护教育活动200余场</li>
                  <li>培训保护专业人员500余人</li>
                </ul>
              </div>
            </div>
            

          </div>
        </section>
      </div>
    </div>
  );
}

export default Plants;